import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import type { DeviceProps } from '#~/device/components/index.js';
import { Section } from '#~/room/components/common/section/index.js';
import type { HtmlAttributes } from '@studio/design-system/props';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type {
  Control,
  DeviceRegistryInterfaceSection
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { DeviceSetting } from '@tryiris-ai/common.protojs/agent/v1/device_settings/device_settings';
import {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryCommandKey,
  type DeviceRegistryInterfaceItem,
  DeviceRegistryInterfaceItemControlType,
  DeviceRegistryInterfaceSectionType
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import { type ReactElement, useCallback } from 'react';
import { twMerge } from 'cn';
import { Controls } from './controls.jsx';
import { DEFAULT_COLLAPSED_SECTIONS } from './hooks.js';
import { sortWeightedItems, useWiring } from './wiring.js';

/** @internal */
export type ControlsProps = DeviceProps &
  HtmlAttributes & {
    mutationOverrides?: Record<string, DeepPartial<DeviceSetting>>;
    onMutation?: (mutation: DeepPartial<Control>) => void;
    // Per ENG-3960 §E12 — canonical device cohort props for edit_control_settings.
    device_model_id?: string;
    device_brand_id?: string;
  } & {
    sectionType: DeviceRegistryInterfaceSectionType;
    variant?: 'inline' | 'sidebar' | 'tray';
  };

/** `<ControlSections>` component. */
export const ControlSections = ({
  className,
  deviceId,
  device_model_id,
  device_brand_id,
  mutationOverrides,
  onMutation,
  sectionType,
  variant = 'sidebar',
  ...props
}: ControlsProps): ReactElement => {
  const {
    agent,
    commandMap,
    isOffline,
    conditionsMap,
    defaultsMap,
    deviceSettings,
    interfaceSections
  } = useWiring(deviceId, mutationOverrides);
  const { track } = useAnalytics();

  // Mapping utility for control labels to event names and categories
  const CONTROL_LABEL_EVENT_MAP: Array<{
    keywords: string[];
    eventName: keyof typeof EVENT_NAMES;
    eventCategory: keyof typeof EVENT_CATEGORIES;
  }> = [
    { keywords: ['focus speed'], eventName: 'CHANGE_FOCUS_SPEED', eventCategory: 'CONTROL_TRAY' },
    { keywords: ['focus', 'AF'], eventName: 'CLICK_FOCUS_SETTINGS', eventCategory: 'CONTROL_TRAY' },
    { keywords: ['exposure'], eventName: 'CLICK_EXPOSURE_SETTINGS', eventCategory: 'CONTROL_TRAY' },
    {
      keywords: ['white balance'],
      eventName: 'CLICK_WHITE_BALANCE_SETTINGS',
      eventCategory: 'CONTROL_TRAY'
    },
    { keywords: ['general'], eventName: 'CLICK_GENERAL_SETTINGS', eventCategory: 'CONTROL_TRAY' }
    // Add more mappings if need be
  ];

  function getEventForControlLabel(label: string): {
    eventName: (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES];
    eventCategory: (typeof EVENT_CATEGORIES)[keyof typeof EVENT_CATEGORIES];
  } {
    const lowerLabel = label.toLowerCase();
    for (const mapping of CONTROL_LABEL_EVENT_MAP) {
      if (mapping.keywords.some((keyword) => lowerLabel.includes(keyword))) {
        return {
          eventName: EVENT_NAMES[mapping.eventName],
          eventCategory: EVENT_CATEGORIES[mapping.eventCategory]
        };
      }
    }
    // Fallback
    return {
      eventName: EVENT_NAMES.EDIT_CONTROL_SETTINGS,
      eventCategory: EVENT_CATEGORIES.CONTROL_TRAY
    };
  }

  const handleControlClick = useCallback(
    (controlData: {
      label: string;
      controlType: DeviceRegistryInterfaceItemControlType;
      key: DeviceRegistryCommandKey;
      value?: DeviceRegistryCommandFunctionType;
    }) => {
      const { eventName, eventCategory } = getEventForControlLabel(controlData.label);
      // Per ENG-3960 §E12 — include canonical device cohort props when available.
      track(eventName, eventCategory, {
        deviceId,
        controlLabel: controlData.label,
        controlKey: DeviceRegistryCommandKey[controlData.key],
        controlValue: controlData.value,
        ...(device_model_id ? { device_model_id } : {}),
        ...(device_brand_id ? { device_brand_id } : {})
      });
    },
    [deviceId, device_model_id, device_brand_id, track]
  );

  const convertSectionItems = (section: DeviceRegistryInterfaceSection) => {
    const convertedItems: Record<string, DeviceRegistryInterfaceItem> = {};
    Object.entries(section.items).forEach(([key, item]) => {
      convertedItems[key] = item as any;
    });
    return convertedItems;
  };

  return (
    <div className={twMerge(className)} {...props}>
      {Object.entries(interfaceSections ?? {})
        .filter(([_, section]) => section.type === sectionType)
        .sort(sortWeightedItems)
        .filter(([_, section]) => Object.entries(section.items).length > 0)
        .map(([id, section]) => (
          <Section
            className={twMerge((variant === 'tray' || variant === 'inline') && 'px-0')}
            key={id}
            slug={section.label ?? ''}
            // Labels in DEFAULT_COLLAPSED_SECTIONS (e.g. Advanced) start
            // collapsed; Section persists a per-user open pref that overrides
            // this initial value on later views.
            defaultOpen={!DEFAULT_COLLAPSED_SECTIONS.has(section.label ?? '')}
            variant={variant}
          >
            <div className='space-y-[16px]'>
              <Controls
                agent={agent}
                className={className}
                commandMap={commandMap}
                isOffline={isOffline}
                conditionsMap={conditionsMap}
                defaultsMap={defaultsMap}
                deviceId={deviceId}
                deviceSettings={deviceSettings}
                items={convertSectionItems(section)}
                onMutation={onMutation}
                onControlClick={handleControlClick}
              />
            </div>
          </Section>
        ))}
    </div>
  );
};
